import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings } from 'lucide-react';
import { useCookie } from '../../contexts/CookieContext';
import { CookieCategory, CookieCategoryInfo } from '../../types';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

const cookieCategories: CookieCategoryInfo[] = [
  {
    id: 'necessary',
    name: 'Cookies Necesarias',
    description: 'Estas cookies son esenciales para el funcionamiento del sitio web y no se pueden desactivar. Incluyen cookies de sesión y seguridad.',
    required: true,
  },
  {
    id: 'analytics',
    name: 'Cookies de Análisis',
    description: 'Estas cookies nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web recopilando información de forma anónima.',
  },
  {
    id: 'marketing',
    name: 'Cookies de Marketing',
    description: 'Estas cookies se utilizan para mostrar anuncios relevantes y medir la efectividad de nuestras campañas publicitarias.',
  },
];

export function CookieSettings() {
  const { 
    preferences, 
    showSettings, 
    closeSettings, 
    acceptSelected, 
    updateCategory 
  } = useCookie();
  
  const [localPreferences, setLocalPreferences] = useState(preferences);

  // 当设置面板打开时，同步本地状态并冻结背景滚动
  useEffect(() => {
    if (showSettings) {
      setLocalPreferences(preferences);
      // 冻结背景滚动
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        // 恢复背景滚动
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showSettings, preferences]);

  const handleToggle = (category: CookieCategory) => {
    if (category === 'necessary') {
      return; // 必要cookie不能禁用
    }
    const newPreferences = {
      ...localPreferences,
      [category]: !localPreferences[category],
    };
    setLocalPreferences(newPreferences);
    updateCategory(category, !localPreferences[category]);
  };

  const handleSave = () => {
    acceptSelected(localPreferences);
  };

  const handleAcceptAll = () => {
    const allAccepted: typeof localPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      consentGiven: true,
      consentDate: new Date().toISOString(),
    };
    acceptSelected(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary: typeof localPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      consentGiven: true,
      consentDate: new Date().toISOString(),
    };
    acceptSelected(onlyNecessary);
  };

  return (
    <AnimatePresence>
      {showSettings && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeSettings}
          />

          {/* 桌面端居中容器 */}
          <div className="fixed inset-0 z-[101] hidden md:flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-[600px] max-h-[85vh] flex flex-col overflow-hidden pointer-events-auto"
              initial={{ 
                opacity: 0,
                scale: 0.9
              }}
              animate={{ 
                opacity: 1,
                scale: 1
              }}
              exit={{ 
                opacity: 0,
                scale: 0.9
              }}
              transition={{ 
                type: 'spring', 
                damping: 30, 
                stiffness: 300,
                duration: 0.4 
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 头部 */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-zwheel-gray-50">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-zwheel-black rounded-lg">
                    <Settings className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-zwheel-black">
                    Configuración de Cookies
                  </h2>
                </div>
                <button
                  onClick={closeSettings}
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                  aria-label="Cerrar configuración"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              {/* 内容区域 */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className="space-y-2">
                  <p className="text-sm text-zwheel-gray-600 leading-relaxed">
                    Utilizamos cookies para mejorar su experiencia, analizar el rendimiento del sitio y personalizar el contenido. 
                    Puede elegir qué tipos de cookies aceptar.
                  </p>
                  <a
                    href="/legal/privacy"
                    className="text-sm text-blue-600 hover:text-blue-700 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Leer más en nuestra Política de Privacidad
                  </a>
                </div>

                {/* Cookie类别列表 */}
                <div className="space-y-4">
                  {cookieCategories.map((category) => {
                    const isEnabled = localPreferences[category.id];
                    const isRequired = category.required;

                    return (
                      <div
                        key={category.id}
                        className={cn(
                          'p-4 rounded-xl border-2 transition-all',
                          isEnabled
                            ? 'border-zwheel-black bg-zwheel-gray-50'
                            : 'border-gray-200 bg-white'
                        )}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="font-semibold text-zwheel-black">
                                {category.name}
                              </h3>
                              {isRequired && (
                                <span className="text-xs px-2 py-0.5 bg-zwheel-black text-white rounded">
                                  Siempre activo
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-zwheel-gray-600 leading-relaxed">
                              {category.description}
                            </p>
                          </div>
                          {!isRequired && (
                            <button
                              onClick={() => handleToggle(category.id)}
                              className={cn(
                                'relative w-12 h-6 rounded-full transition-colors duration-300 flex-shrink-0 ml-4',
                                isEnabled ? 'bg-zwheel-black' : 'bg-gray-300'
                              )}
                              aria-label={`${isEnabled ? 'Desactivar' : 'Activar'} ${category.name}`}
                            >
                              <span
                                className={cn(
                                  'absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform duration-300',
                                  isEnabled ? 'translate-x-6' : 'translate-x-0'
                                )}
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 底部按钮 */}
              <div className="p-6 border-t border-gray-200 bg-zwheel-gray-50 space-y-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="outline"
                    onClick={handleRejectAll}
                    className="flex-1"
                  >
                    Rechazar Todas
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleAcceptAll}
                    className="flex-1"
                  >
                    Aceptar Todas
                  </Button>
                </div>
                <Button
                  onClick={handleSave}
                  className="w-full bg-zwheel-black hover:bg-zwheel-gray-800 text-white"
                >
                  Guardar Preferencias
                </Button>
              </div>
            </motion.div>
          </div>

          {/* 移动端设置面板 */}
          <motion.div
            className={cn(
              "fixed bottom-0 left-0 right-0 z-[101] bg-white rounded-t-2xl shadow-2xl",
              "w-full max-h-[90vh] flex flex-col overflow-hidden md:hidden"
            )}
            initial={{ 
              y: '100%', 
              opacity: 0
            }}
            animate={{ 
              y: 0, 
              opacity: 1
            }}
            exit={{ 
              y: '100%', 
              opacity: 0
            }}
            transition={{ 
              type: 'spring', 
              damping: 30, 
              stiffness: 300,
              duration: 0.4 
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-zwheel-gray-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-zwheel-black rounded-lg">
                  <Settings className="h-5 w-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-zwheel-black">
                  Configuración de Cookies
                </h2>
              </div>
              <button
                onClick={closeSettings}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                aria-label="Cerrar configuración"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* 内容区域 */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="space-y-2">
                <p className="text-sm text-zwheel-gray-600 leading-relaxed">
                  Utilizamos cookies para mejorar su experiencia, analizar el rendimiento del sitio y personalizar el contenido. 
                  Puede elegir qué tipos de cookies aceptar.
                </p>
                <a
                  href="/legal/cookies"
                  className="text-sm text-blue-600 hover:text-blue-700 underline"
                >
                  Leer más en nuestra Política de Cookies
                </a>
              </div>

              {/* Cookie类别列表 */}
              <div className="space-y-4">
                {cookieCategories.map((category) => {
                  const isEnabled = localPreferences[category.id];
                  const isRequired = category.required;

                  return (
                    <div
                      key={category.id}
                      className={cn(
                        'p-4 rounded-xl border-2 transition-all',
                        isEnabled
                          ? 'border-zwheel-black bg-zwheel-gray-50'
                          : 'border-gray-200 bg-white'
                      )}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="font-semibold text-zwheel-black">
                              {category.name}
                            </h3>
                            {isRequired && (
                              <span className="text-xs px-2 py-0.5 bg-zwheel-black text-white rounded">
                                Siempre activo
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-zwheel-gray-600 leading-relaxed">
                            {category.description}
                          </p>
                        </div>
                        {!isRequired && (
                          <button
                            onClick={() => handleToggle(category.id)}
                            className={cn(
                              'relative w-12 h-6 rounded-full transition-colors duration-300 flex-shrink-0 ml-4',
                              isEnabled ? 'bg-zwheel-black' : 'bg-gray-300'
                            )}
                            aria-label={`${isEnabled ? 'Desactivar' : 'Activar'} ${category.name}`}
                          >
                            <span
                              className={cn(
                                'absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform duration-300',
                                isEnabled ? 'translate-x-6' : 'translate-x-0'
                              )}
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 底部按钮 */}
            <div className="p-6 border-t border-gray-200 bg-zwheel-gray-50 space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={handleRejectAll}
                  className="flex-1"
                >
                  Rechazar Todas
                </Button>
                <Button
                  variant="outline"
                  onClick={handleAcceptAll}
                  className="flex-1"
                >
                  Aceptar Todas
                </Button>
              </div>
              <Button
                onClick={handleSave}
                className="w-full bg-zwheel-black hover:bg-zwheel-gray-800 text-white"
              >
                Guardar Preferencias
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

